import React, { useState, KeyboardEvent } from 'react';
import { TagListParams } from './types';
import { Trash2 } from 'react-feather';
import { addTag, deleteTag } from '../../api';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useGeneralContext } from '../../context';
import { OverlayTrigger, Popover } from 'react-bootstrap';

interface NewTagInputParams {
  creationDate: string;
  entryTags: string[];
  darkMode: boolean;
  blur: boolean;
  setTags: (tags: string[]) => void;
}

const NewTagInput = (params: NewTagInputParams) => {
  const { creationDate, entryTags, darkMode, blur, setTags } = params;
  const [focus, setFocus] = useState(false);
  const [newTagInput, setNewTagInput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const context = useGeneralContext();
  const { allTags } = context;

  const validTags = allTags.filter((tag) => tag !== null && !entryTags.includes(tag));

  const saveNewTag = async (value: string, event: KeyboardEvent) => {
    // TODO seperate saving of tags from journal entries ?
    setNewTagInput('');
    if (!value || entryTags.indexOf(value) !== -1) {
      return;
    }

    try {
      await addTag({
        creationDate,
        value
      });
      setTags([...entryTags, value]);
    } catch (error: any) {
      if (error instanceof AxiosError && error?.response?.status === 404) {
        window.alert('Unable to save because you have been logged out. Please log back in.');
        history.push('/');
      }
    }
    (event.target as HTMLInputElement).value = '';
  };

  // a hack to catch clicks in the list after onchange updates the state
  const handleKeyUp = async (event: KeyboardEvent) => {
    const listClicked = event.code === undefined;
    if (listClicked) {
      setFocus(false);
      setLoading(true);
      await saveNewTag(newTagInput, event);
      setLoading(false);
    }
  };

  const handleKeyDown = async (event: any) => {
    if (event.key !== 'Enter') {
      return;
    }

    const value: string = event.target.value && event.target.value.trim();
    event.target.value = '';
    setFocus(false);
    setLoading(true);
    await saveNewTag(value, event);
    setLoading(false);
  };

  const onFocus = () => {
    setFocus(true);
    setNewTagInput('');
  };

  const onBlur = () => {
    setFocus(false);
    setNewTagInput('');
  };

  const onChange = (event: any) => {
    setNewTagInput(event.target.value);
  };

  return loading ? (
    <div className="tw-w-28 tw-h-8 tw-flex tw-flex-row tw-items-center tw-justify-center">
      <span className="spinner-border tw-h-4 tw-w-4" role="status" aria-hidden="true"></span>
    </div>
  ) : (
    <li key="new-tag" className="tw-h-8 tw-flex tw-flex-col tw-justify-center">
      <input
        // tells mobile to display a 'go' button which acts like 'enter' on a keyboard
        inputMode="search"
        key="input"
        value={newTagInput}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        list="tags"
        type="text"
        placeholder={focus ? '' : '+ add tag'}
        className={`
        ${blur && 'tw-blur'}
        ${darkMode ? 'tw-text-neutral-50' : 'tw-text-primary-900'}
        ${
          focus
            ? 'tw-bg-transparent tw-rounded-full tw-border tw-border-neutral-500 tw-px-3 tw-min-w-[8rem] tw-h-8 tw-flex tw-justify-center tw-items-center'
            : 'tw-w-28 tw-h-8 tw-bg-neutral-600 tw-rounded-full tw-border-none tw-text-center hover:tw-cursor-pointer'
        } tw-text-sm tw-font-semibold placeholder:tw-text-white`}
      />
      <datalist id="tags">
        {validTags.map((tag, i) => (
          <option key={`${tag}-${i}`}>{tag}</option>
        ))}
      </datalist>
    </li>
  );
};

export const TagList = (params: TagListParams) => {
  const { creationDate, entryTagStrings, darkMode, blur, setTags, autoGeneratedTags } = params;
  const [indexesLoading, setLoadingIndexes] = useState<number[]>([]);

  const [mousedOverTagIndex, setMousedOverTagIndex] = useState<number | null>(null);

  return (
    <ul className="tw-flex tw-flex-wrap tw-gap-2 tw-p-0 tw-list-none">
      <NewTagInput
        darkMode={darkMode}
        creationDate={creationDate}
        entryTags={entryTagStrings}
        blur={blur}
        setTags={setTags}
      />
      {entryTagStrings.map((tag, index) => (
        <li key={index.toString()}>
          {indexesLoading.includes(index) ? (
            <div className="tw-w-[104px] tw-flex tw-flex-row tw-items-center tw-content-center tw-justify-around tw-flex-wrap">
              <span
                style={{ color: 'red', height: '1vw', width: '1vw' }}
                className="spinner-border"
                role="status"
                aria-hidden="true"
              ></span>
            </div>
          ) : (
            <button
              onMouseEnter={() => setMousedOverTagIndex(index)}
              onMouseLeave={() => setMousedOverTagIndex(null)}
              title={tag}
              className={`${
                blur && 'tw-blur'
              } tw-bg-transparent tw-rounded-[38px] tw-border tw-border-neutral-500 tw-p-2 tw-min-w-[125px] tw-h-[30px] tw-flex tw-justify-center tw-items-center hover:tw-bg-secondary-100 hover:tw-text-black tw-text-base tw-font-semibold ${
                darkMode ? 'tw-text-neutral-100' : 'tw-text-primary-900'
              }`}
              onClick={async () => {
                if (window.confirm(`Delete "${tag}" tag?`)) {
                  setLoadingIndexes([...indexesLoading, index]);

                  await deleteTag({
                    value: tag,
                    creationDate
                  });
                  setTags(entryTagStrings.filter((t) => t !== tag));
                  setLoadingIndexes([
                    ...indexesLoading.slice(0, indexesLoading.indexOf(index)),
                    ...indexesLoading.slice(indexesLoading.indexOf(index) + 1, indexesLoading.length)
                  ]);
                }
              }}
            >
              {mousedOverTagIndex === index ? (
                <div>
                  <Trash2 size={18} />
                </div>
              ) : (
                trim(tag)
              )}
            </button>
          )}
        </li>
      ))}
      {autoGeneratedTags.map((tag, index) => (
        <li key={`autogenerated-${index.toString()}`}>
          <OverlayTrigger
            overlay={
              <Popover id="popover-basic">
                {/* <Popover.Header as="h3">Autogenerated Tag</Popover.Header> */}
                <Popover.Body>Autogenerated tag</Popover.Body>
              </Popover>
            }
            delay={0}
            placement="bottom"
          >
            <div
              title={tag}
              className={`${
                blur && 'tw-blur'
              } tw-rounded-[38px] tw-border tw-border-neutral-500 tw-p-2 tw-min-w-[125px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-bg-neutral-200 tw-text-base tw-font-semibold ${
                darkMode ? 'tw-text-neutral-100' : 'tw-text-primary-900'
              }`}
            >
              {trim(tag)}
            </div>
          </OverlayTrigger>
        </li>
      ))}
    </ul>
  );
};

const trim = (tag: string) => {
  if (!tag) {
    return tag;
  }
  return tag.length > 12 ? tag.slice(0, 9) + '...' : tag;
};
